import { type DatiSessioneUtente } from '@framework/api-client/api/users/schemas/datiSessioneUtente.ts';
import {
  createListenerMiddleware,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { getAuthGlobalOptions } from '../options.ts';
import { PermissionLogic } from '../permissions/PermissionLogic.ts';
import { type PermissionType } from '../permissions/PermissionType.ts';
import { AuthStorage } from '../storage/AuthStorage.ts';

export interface AuthState {
  isLogged: boolean;
  isPrelogged: boolean;
  strategy: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  officeId?: string | null;
  roleId?: string | null;
  isAccessTokenExpired?: boolean;
  logoutUrls: string[] | null;
  isRefreshTokenExpired: boolean;
  current: DatiSessioneUtente | null;
  /** Dati esterni dell'utente, recuperati da servizi terzi (gestionale, documentale, DB del personale) */
  userData?: Record<string, any> | null;
  fetchCount: number;
  debugMode?: boolean;
  simulateExpiredRefreshToken?: boolean;
}

/**
 * Interfaccia parziale per ottenere lo stato dell'autenticazione dal Root.
 */
export interface IRootAuthState {
  auth: AuthState;
}

export const AuthSlice = createSlice({
  name: 'auth',
  initialState: () =>
    ({
      isRefreshTokenExpired: false,
      current: null,
      accessToken: null,
      fetchCount: 0,
      isAccessTokenExpired: false,
      /**
       * Carichiamo i dati iniziali dal `sessionStorage` in caso siano
       * presenti.
       */
      ...AuthStorage.initialState(),
    }) as AuthState,
  reducers: {
    /**
     * Al login, salviamo il token.
     */
    loginSucceed: (
      state,
      action: PayloadAction<{
        strategy: string;
        accessToken: string;
        refreshToken: string | null;
        logoutUrls?: string[] | null;
      }>,
    ) => {
      const { requiresRoleSelection } = getAuthGlobalOptions();
      const { strategy, accessToken, refreshToken, logoutUrls } =
        action.payload;
      const update = {
        strategy,
        refreshToken,
        isLogged: !requiresRoleSelection,
        isPrelogged: true,
        userData: state.userData,
        logoutUrls: process.env.MOCK_LOGOUT_URL
          ? [process.env.MOCK_LOGOUT_URL]
          : (logoutUrls ?? null),
      };
      AuthStorage.saveState(update);
      return {
        ...state,
        ...update,
        accessToken,
        isAccessTokenExpired: false,
        isRefreshTokenExpired: false,
        fetchCount: state.fetchCount + 1,
        current: null,
      };
    },
    /**
     * Rimozione del token dalla sessione.
     */
    logout: (
      state,
      action: PayloadAction<{ isRefreshTokenExpired?: boolean } | undefined>,
    ) => {
      const update = {
        strategy: null,
        isPrelogged: false,
        isLogged: false,
        logoutUrls: null,
        refreshToken: null,
        officeId: null,
        roleId: null,
      };
      AuthStorage.saveState(update);
      return {
        ...state,
        ...update,
        accessToken: null,
        isRefreshTokenExpired: action.payload?.isRefreshTokenExpired ?? false,
        current: null,
      };
    },
    /**
     * Utente corrente caricato. Quando questa richiesta ha esito positivo,
     * vuol dire che l'utente è autorizzato quindi possiamo impostare il token.
     */
    currentLoaded: (
      state,
      action: PayloadAction<{ current: DatiSessioneUtente }>,
    ) => {
      const { requiresRoleSelection } = getAuthGlobalOptions();
      const { roleId, officeId } = PermissionLogic.getSelectedOfficeAndRole(
        action.payload.current,
      );
      const update = {
        refreshToken: state.refreshToken,
        strategy: state.strategy,
        isLogged:
          !requiresRoleSelection || (officeId != null && roleId != null),
        isPrelogged: true,
        roleId: requiresRoleSelection ? roleId : null,
        officeId: requiresRoleSelection ? officeId : null,
        userData: state.userData,
        logoutUrls: state.logoutUrls,
      };
      AuthStorage.saveState(update);
      return {
        ...state,
        ...update,
        current: action.payload.current,
      };
    },
    /**
     * Token ricaricato.
     */
    tokenRefreshed: (
      state,
      action: PayloadAction<{
        accessToken: string;
        refreshToken: string;
      }>,
    ) => {
      const { accessToken, refreshToken } = action.payload;
      const update = {
        strategy: state.strategy,
        refreshToken,
        isLogged: true, // TODO Verificare non è detto che sia vero.
        isPrelogged: true,
        userData: state.userData,
        logoutUrls: state.logoutUrls,
      };
      AuthStorage.saveState(update);
      return {
        ...state,
        ...update,
        isAccessTokenExpired: false,
        accessToken,
      };
    },
    /**
     * Access token scaduto, per richieste in background (polling)
     */
    accessTokenExpired: state => {
      return {
        ...state,
        isAccessTokenExpired: true,
      };
    },
    simulateExpiredRefreshToken: (
      state,
      { payload: flag = true }: PayloadAction<boolean | undefined>,
    ) => ({
      ...state,
      simulateExpiredRefreshToken: flag,
    }),
    userDataLoaded: (state, action: PayloadAction<Record<string, any>>) => ({
      ...state,
      userData: { ...(state.userData ?? {}), ...action.payload },
    }),
  },
});

export const AuthActions = AuthSlice.actions;

/**
 * Hook per sapere se si è loggati.
 */
export const useAuth = () =>
  useSelector(
    (state: IRootAuthState) => ({
      isLogged: state.auth.isLogged,
      isPrelogged: state.auth.isPrelogged,
      strategy: state.auth.strategy,
      refreshToken: state.auth.refreshToken,
      accessToken: state.auth.accessToken,
      fetchCount: state.auth.fetchCount,
      logoutUrls: state.auth.logoutUrls,
    }),
    shallowEqual,
  );

export type UseAuthResult = ReturnType<typeof useAuth>;

export const useCurrentUser = () =>
  useSelector((state: IRootAuthState) => state.auth.current, shallowEqual);

/**
 * Hook per controllare i permessi
 */
export const usePermissions = () => {
  const user = useCurrentUser();
  return {
    has(permission: PermissionType | PermissionType[]) {
      return PermissionLogic.has(user, permission);
    },
  };
};

export const useOnRefreshTokenExpired = (onExpired: () => void) => {
  const isRefreshTokenExpired = useSelector(
    (state: IRootAuthState) => state.auth.isRefreshTokenExpired,
    shallowEqual,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <tbd>
  useEffect(() => {
    if (isRefreshTokenExpired) {
      onExpired();
    }
  }, [isRefreshTokenExpired]);
};

export const useCurrentUserData = () =>
  useSelector(
    (state: IRootAuthState) => state.auth.userData ?? {},
    shallowEqual,
  );

export const logoutListnerMiddleware =
  createListenerMiddleware<IRootAuthState>();
logoutListnerMiddleware.startListening({
  actionCreator: AuthSlice.actions.logout,
  effect: (action, listnerApi) => {
    // Lasciato middleware configurato per eventuali modficiche future
  },
});
